import { ApiClient } from '@center-technologies/shared-api-clients';

interface NotificationStats {
  total: number;
  pending: number;
  sent: number;
  read: number;
  failed: number;
}

type StatsCallback = (stats: NotificationStats, unreadCount: number) => void;
type ErrorCallback = (error: Error) => void;

interface Subscriber {
  id: string;
  onStats: StatsCallback;
  onError?: ErrorCallback;
  /** The interval this subscriber would poll at on its own, if it had to. */
  requestedIntervalMs?: number;
}

export interface SubscribeOptions {
  /**
   * How often this subscriber needs stats. The service polls at the *smallest*
   * interval any subscriber asks for (floored at `BASE_POLL_INTERVAL_MS`), so a
   * subscriber that wants a slower cadence never forces extra requests, and one
   * that wants a faster cadence never gets starved.
   */
  pollIntervalMs?: number;
}

/**
 * Singleton service to manage notification polling across multiple components.
 * Prevents duplicate polling intervals when multiple NotificationBell components are mounted.
 * Includes exponential backoff when the notification service is unavailable.
 */
/**
 * The stats object out of either response shape — `{ success, data }` from the
 * service, or the bare stats object when an axios interceptor already unwrapped
 * it. `null` when the payload is neither.
 */
function readStatsPayload(data: unknown): NotificationStats | null {
  if (!data || typeof data !== 'object') return null;
  const payload = data as { success?: boolean; data?: NotificationStats; total?: unknown };
  if (payload.success && payload.data) return payload.data;
  if ('total' in payload) return payload as unknown as NotificationStats;
  return null;
}

class NotificationPollingService {
  private static instance: NotificationPollingService;
  private readonly subscribers = new Map<string, Subscriber>();
  private pollingInterval?: NodeJS.Timeout;
  private currentUserId?: string;
  private notificationClient?: ApiClient;
  private isPolling = false;

  // Polling interval configuration
  private readonly BASE_POLL_INTERVAL_MS = 10000; // 10 seconds
  private readonly MAX_POLL_INTERVAL_MS = 300000; // 5 minutes max backoff
  private currentPollIntervalMs = 10000;

  // Circuit breaker state
  private consecutiveFailures = 0;
  private readonly CIRCUIT_OPEN_THRESHOLD = 5; // Open circuit after 5 consecutive failures
  private isCircuitOpen = false;

  // The last successful result and the fetch currently in flight. A subscriber
  // that arrives while one is in flight, or right after one completed, is served
  // from these instead of triggering its own request: several components mount
  // together on a page and each used to cost a stats round trip.
  private lastResult?: { stats: NotificationStats; unreadCount: number; userId: string; fetchedAt: number };
  private inFlightFetch?: Promise<void>;
  /** The identity `inFlightFetch` was started for. A fetch minted under one
   *  identity must never be joined — or delivered — by another. */
  private inFlightUserId?: string;
  /** How recent a result must be to hand to a new subscriber without a fetch. */
  private readonly FRESH_RESULT_MS = 2000;

  private constructor() {
    // Private constructor for singleton
  }

  static getInstance(): NotificationPollingService {
    if (!NotificationPollingService.instance) {
      NotificationPollingService.instance = new NotificationPollingService();
    }
    return NotificationPollingService.instance;
  }

  /**
   * Subscribe to notification updates
   */
  subscribe(
    subscriberId: string,
    userId: string,
    notificationClient: ApiClient,
    onStats: StatsCallback,
    onError?: ErrorCallback,
    options?: SubscribeOptions
  ): void {
    // Add subscriber
    this.subscribers.set(subscriberId, {
      id: subscriberId,
      onStats,
      onError,
      requestedIntervalMs: options?.pollIntervalMs,
    });

    // Update client and user info. On an identity change (impersonation start
    // or stop) the previous identity's counts are not this subscriber's to see,
    // so drop the cached result rather than hand it over below.
    if (this.currentUserId !== userId) {
      this.lastResult = undefined;
    }
    this.currentUserId = userId;
    this.notificationClient = notificationClient;

    if (!this.isPolling) {
      this.startPolling();
      return;
    }

    this.applyNegotiatedInterval();

    // Already polling: a fetch in flight will notify this subscriber when it
    // lands; a fresh result is handed over directly; otherwise fetch now.
    if (this.inFlightFetch && this.inFlightUserId === userId) return;
    const last = this.lastResult;
    if (last && last.userId === userId && Date.now() - last.fetchedAt <= this.FRESH_RESULT_MS) {
      this.deliver(this.subscribers.get(subscriberId), last.stats, last.unreadCount);
      return;
    }
    this.fetchStats();
  }

  /**
   * The poll cadence the current subscriber set implies: the fastest anyone asked
   * for, never faster than the service's own floor. Not applied while backed off —
   * the circuit breaker owns the interval in that state.
   */
  private negotiatedIntervalMs(): number {
    let fastest = Number.POSITIVE_INFINITY;
    for (const subscriber of this.subscribers.values()) {
      if (subscriber.requestedIntervalMs && subscriber.requestedIntervalMs > 0) {
        fastest = Math.min(fastest, subscriber.requestedIntervalMs);
      }
    }
    if (!Number.isFinite(fastest)) return this.BASE_POLL_INTERVAL_MS;
    return Math.max(fastest, this.BASE_POLL_INTERVAL_MS);
  }

  private applyNegotiatedInterval(): void {
    if (!this.isPolling || this.consecutiveFailures > 0 || this.isCircuitOpen) return;
    const target = this.negotiatedIntervalMs();
    if (target !== this.currentPollIntervalMs) {
      this.restartPollingWithInterval(target);
    }
  }

  /** `subscriber` is optional because callers resolve it from the map, which
   *  can miss if it unsubscribed between lookup and delivery. */
  private deliver(subscriber: Subscriber | undefined, stats: NotificationStats, unreadCount: number): void {
    if (!subscriber) return;
    try {
      subscriber.onStats(stats, unreadCount);
    } catch (error) {
      console.error(`[NotificationPollingService] Error in subscriber ${subscriber.id} callback:`, error);
    }
  }

  /**
   * Unsubscribe from notification updates
   */
  unsubscribe(subscriberId: string): void {
    this.subscribers.delete(subscriberId);

    // Stop polling if no more subscribers
    if (this.subscribers.size === 0) {
      this.stopPolling();
      return;
    }

    // The subscriber that set the fastest cadence may have just left.
    this.applyNegotiatedInterval();
  }

  /**
   * Force an immediate stats fetch (useful after marking notifications as read)
   */
  refresh(): Promise<void> {
    if (!this.isPolling) return Promise.resolve();

    // The caller has just mutated something and wants the counts that follow it.
    // A fetch already in flight may have been minted BEFORE that mutation, so
    // joining it can hand back pre-mutation counts. Drop the cached result and
    // chain a new fetch behind the pending one instead of sharing it.
    this.lastResult = undefined;

    const pending = this.inFlightFetch;
    if (pending) return pending.then(() => this.fetchStats());
    return this.fetchStats();
  }

  private startPolling(): void {
    if (this.isPolling) return;

    this.isPolling = true;
    this.currentPollIntervalMs = this.negotiatedIntervalMs();

    // Fetch immediately
    this.fetchStats();

    // Then poll at the current interval
    this.pollingInterval = setInterval(() => {
      this.fetchStats();
    }, this.currentPollIntervalMs);
  }

  /**
   * Restart polling with a new interval (used for backoff/recovery)
   */
  private restartPollingWithInterval(intervalMs: number): void {
    if (!this.isPolling) return;

    // Clear existing interval
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }

    this.currentPollIntervalMs = intervalMs;
    console.log(`[NotificationPollingService] Adjusting poll interval to ${intervalMs}ms`);

    // Start new interval
    this.pollingInterval = setInterval(() => {
      this.fetchStats();
    }, this.currentPollIntervalMs);
  }

  /**
   * Handle successful fetch - reset backoff state
   */
  private onFetchSuccess(): void {
    if (this.consecutiveFailures > 0 || this.isCircuitOpen) {
      console.log(`[NotificationPollingService] Service recovered after ${this.consecutiveFailures} failures, resetting to normal polling`);
      this.consecutiveFailures = 0;
      this.isCircuitOpen = false;

      // Reset to the cadence the subscriber set asks for, not the raw floor.
      const target = this.negotiatedIntervalMs();
      if (this.currentPollIntervalMs !== target) {
        this.restartPollingWithInterval(target);
      }
    }
  }

  /**
   * Handle failed fetch - apply exponential backoff
   */
  private onFetchFailure(): void {
    this.consecutiveFailures++;

    // Calculate new interval with exponential backoff
    const newInterval = Math.min(
      this.BASE_POLL_INTERVAL_MS * Math.pow(2, this.consecutiveFailures),
      this.MAX_POLL_INTERVAL_MS
    );

    // Check if circuit should open
    if (this.consecutiveFailures >= this.CIRCUIT_OPEN_THRESHOLD && !this.isCircuitOpen) {
      this.isCircuitOpen = true;
      console.warn(`[NotificationPollingService] Circuit OPEN after ${this.consecutiveFailures} consecutive failures. Backing off to ${this.MAX_POLL_INTERVAL_MS}ms`);
      this.restartPollingWithInterval(this.MAX_POLL_INTERVAL_MS);
    } else if (newInterval !== this.currentPollIntervalMs) {
      console.warn(`[NotificationPollingService] Fetch failed (${this.consecutiveFailures} consecutive). Backing off to ${newInterval}ms`);
      this.restartPollingWithInterval(newInterval);
    }
  }

  private stopPolling(): void {
    this.isPolling = false;

    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = undefined;
    }
  }

  private fetchStats(): Promise<void> {
    if (!this.currentUserId || !this.notificationClient || this.subscribers.size === 0) {
      return Promise.resolve();
    }
    // One request at a time; every caller during that window shares it.
    if (this.inFlightFetch && this.inFlightUserId === this.currentUserId) return this.inFlightFetch;
    this.inFlightUserId = this.currentUserId;
    // An identity change starts a second fetch while the first is still in
    // flight, so a settling fetch must only clear the slot it still owns —
    // otherwise the older one retires the newer one's registration and the
    // next caller starts a duplicate request instead of joining it.
    const fetch: Promise<void> = this.fetchStatsNow().finally(() => {
      if (this.inFlightFetch !== fetch) return;
      this.inFlightFetch = undefined;
      this.inFlightUserId = undefined;
    });
    this.inFlightFetch = fetch;
    return fetch;
  }

  private async fetchStatsNow(): Promise<void> {
    const userId = this.currentUserId!;
    try {
      // No retries - circuit breaker handles backoff at the polling level
      const response = await this.notificationClient!.get(
        `/api/notifications/${userId}/stats`,
        { retries: 0 }
      );

      const stats = readStatsPayload(response.data);
      if (!stats) return;

      // The identity changed while this request was in flight; these counts
      // belong to whoever was signed in when it started, not to the current
      // subscribers. Drop them rather than caching or delivering them.
      if (userId !== this.currentUserId) return;

      // Success - reset backoff state
      this.onFetchSuccess();

      // Count pending and sent as unread
      const unreadCount = (stats.pending || 0) + (stats.sent || 0);
      this.lastResult = { stats, unreadCount, userId, fetchedAt: Date.now() };

      // Notify all subscribers
      for (const subscriber of this.subscribers.values()) {
        this.deliver(subscriber, stats, unreadCount);
      }
    } catch (error) {
      // Apply exponential backoff on failure
      this.onFetchFailure();

      // Only log if not in circuit-open state (to reduce log spam)
      if (!this.isCircuitOpen || this.consecutiveFailures === this.CIRCUIT_OPEN_THRESHOLD) {
        console.error('[NotificationPollingService] Failed to fetch notification stats:', error);
      }

      this.deliverError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  /** Fan a fetch failure out to every subscriber that asked for it; one throwing callback never blocks the rest. */
  private deliverError(error: Error): void {
    for (const subscriber of this.subscribers.values()) {
      if (!subscriber.onError) continue;
      try {
        subscriber.onError(error);
      } catch (callbackError) {
        console.error(`[NotificationPollingService] Error in subscriber ${subscriber.id} error callback:`, callbackError);
      }
    }
  }

  /**
   * Get current polling stats for debugging
   */
  getStats() {
    return {
      isPolling: this.isPolling,
      subscriberCount: this.subscribers.size,
      subscriberIds: Array.from(this.subscribers.keys()),
      currentPollIntervalMs: this.currentPollIntervalMs,
      consecutiveFailures: this.consecutiveFailures,
      isCircuitOpen: this.isCircuitOpen,
    };
  }
}

// Export singleton instance
export const notificationPollingService = NotificationPollingService.getInstance();
export type { NotificationStats };
